import mixpanel from 'mixpanel-browser'
import * as moment from 'moment'
import Cookies from 'universal-cookie'

import SecurityService from '../services/SecurityService'
import { analyticsUserType } from '../constants/userConstants'
const securitySvc = SecurityService.singletonInstance()

const cookies = new Cookies()

let isInitialized = false

const initMixpanel = async () => {
  if (isInitialized) {
    return true
  }
  try {
    const decrypted = await securitySvc.retrieveSensitiveData('92469f8a-4e32-4ca6-9df2-20774c98286c')

    mixpanel.init(decrypted, {
      track_pageview: false,
      debug: process.env.NODE_ENV === 'development',
      persistence: 'localStorage'
    })
    isInitialized = true
    return true
  } catch (err) {
    console.error(err)
    return false
  }
}

const checkMixpanelThenRun = thenRun => {
  if (!isInitialized) {
    initMixpanel().then(result => {
      if (result) {
        thenRun()
      }
    })
  } else {
    thenRun()
  }
}

const mixpanelAliasUser = user => {
  try {
    if (user) {
      checkMixpanelThenRun(() => {
        mixpanel.alias(user._id)
      })
    }
  } catch (err) {
    console.error(err)
  }
}
const mixpanelIdentifyUser = (user, inTeams) => {
  const region = cookies.get('x-region-admin')

  try {
    if (user) {
      checkMixpanelThenRun(() => {
        mixpanel.identify(user._id)
      })
    }
  } catch (err) {
    console.error(err)
  }
}

const mixpanelSwitchToLiveEnvironment = () => {
  try {
    checkMixpanelThenRun(() => {
      mixpanel.register({
        environment: 'live'
      })
    })
  } catch (err) {
    console.error(err)
  }
}

const mixpanelPageViewEvent = (page, properties = {}) => {
  try {
    checkMixpanelThenRun(() => {
      mixpanel.track_pageview({ page: page, projectType: 'admin', ...properties })
    })
  } catch (e) {
    console.error(e)
  }
}

const mixpanelMenuClickedEvent = (buttonName, properties = {}) => {
  try {
    const urlName = window.location.href.split('#').pop()
    checkMixpanelThenRun(() => {
      mixpanel.track('Menu Clicked', {
        buttonName: buttonName,
        openedFrom: 'Navbar',
        pageUrl: urlName,
        projectType: 'admin',
        ...properties
      })
    })
  } catch (err) {
    console.error(err)
  }
}

const mixpanelButtonClickedEvent = (buttonName, openedFrom, properties = {}) => {
  try {
    const urlName = window.location.href.split('#').pop()
    checkMixpanelThenRun(() => {
      mixpanel.track('Button Clicked', {
        buttonName: buttonName,
        openedFrom: openedFrom,
        pageUrl: urlName,
        projectType: 'admin',
        ...properties
      })
    })
  } catch (err) {
    console.error(err)
  }
}

/**
 * `mixpanelButtonClickedEvent` the caller can await before navigating away.
 *
 * The plain variant is fire-and-forget, which loses the event on any handler that assigns
 * `window.location.href` — and here that is guaranteed, because `initMixpanel` first fetches
 * the project token over the network, so nothing is even queued at click time. Sends via
 * sendBeacon so an in-flight request survives the unload, and resolves on a timeout rather
 * than rejecting, so analytics can never block a sign-in redirect.
 *
 * @param   {string} buttonName
 * @param   {string} openedFrom
 * @param   {Object} [properties]
 * @param   {number} [timeoutMs] - Upper bound on how long a caller waits before redirecting.
 * @returns {Promise<boolean>} True when the event was sent; false on timeout or error. Never rejects.
 */
const mixpanelButtonClickedEventPromise = (buttonName, openedFrom, properties = {}, timeoutMs = 1000) => {
  return new Promise(resolve => {
    let settled = false
    const done = ok => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(ok)
    }
    const timer = setTimeout(() => done(false), timeoutMs)
    try {
      const urlName = window.location.href.split('#').pop()
      checkMixpanelThenRun(() => {
        mixpanel.track(
          'Button Clicked',
          {
            buttonName: buttonName,
            openedFrom: openedFrom,
            pageUrl: urlName,
            projectType: 'admin',
            ...properties
          },
          {
            send_immediately: true,
            transport: 'sendBeacon'
          },
          () => done(true)
        )
      })
    } catch (err) {
      console.error(err)
      done(false)
    }
  })
}

const mixpanelTabClickedEvent = (buttonName, openedFrom, properties = {}) => {
  try {
    const urlName = window.location.href.split('#').pop()
    checkMixpanelThenRun(() => {
      mixpanel.track('Tab Clicked', {
        buttonName: buttonName,
        openedFrom: openedFrom,
        pageUrl: urlName,
        projectType: 'admin',
        ...properties
      })
    })
  } catch (err) {
    console.error(err)
  }
}

const mixpanelSubmitClickedEvent = (buttonName, openedFrom, properties = {}) => {
  try {
    const urlName = window.location.href.split('#').pop()
    checkMixpanelThenRun(() => {
      mixpanel.track('Submit Clicked', {
        buttonName: buttonName,
        openedFrom: openedFrom,
        projectType: 'admin',
        pageUrl: urlName,
        ...properties
      })
    })
  } catch (err) {
    console.error(err)
  }
}

const mixpanelOpenClickedEvent = (buttonName, openedFrom, properties = {}) => {
  try {
    const urlName = window.location.href.split('#').pop()
    checkMixpanelThenRun(() => {
      mixpanel.track('Open Clicked', {
        buttonName: buttonName,
        openedFrom: openedFrom,
        projectType: 'admin',
        pageUrl: urlName,
        ...properties
      })
    })
  } catch (err) {
    console.error(err)
  }
}

const mixpanelLoginEvent = () => {
  try {
    checkMixpanelThenRun(() => {
      mixpanel.track('Login')
    })
  } catch (err) {
    console.error(err)
  }
}

const mixpanelLogoutEvent = () => {
  try {
    checkMixpanelThenRun(() => {
      mixpanel.track('Logout')
      resetMixpanel()
    })
  } catch (err) {
    console.error(err)
  }
}

const resetMixpanel = () => {
  isInitialized = false
  mixpanel.reset()
}

const mixpanelCustomEvent = (eventName, properties = {}) => {
  try {
    checkMixpanelThenRun(() => {
      mixpanel.track(eventName, {
        projectType: 'admin',
        ...properties
      })
    })
  } catch (err) {
    console.error(err)
  }
}

export {
  initMixpanel,
  mixpanelMenuClickedEvent,
  resetMixpanel,
  mixpanelButtonClickedEvent,
  mixpanelButtonClickedEventPromise,
  mixpanelTabClickedEvent,
  mixpanelSubmitClickedEvent,
  mixpanelOpenClickedEvent,
  mixpanelAliasUser,
  mixpanelIdentifyUser,
  mixpanelLoginEvent,
  mixpanelLogoutEvent,
  mixpanelPageViewEvent,
  mixpanelSwitchToLiveEnvironment,
  mixpanelCustomEvent
}
